/**
 * Generic Confirmation Modal Component
 * Provides a reusable modal for confirmations, warnings, and multi-option dialogs
 */

export interface ModalButton {
  label: string;
  value: string;
  style: 'primary' | 'secondary' | 'danger';
  isDefault?: boolean;
}

export interface ModalConfig {
  title: string;
  message: string;
  modalType?: 'warning' | 'info' | 'danger';
  buttons: ModalButton[];
  allowBackgroundClose?: boolean;
}

/**
 * Show a confirmation modal and return the selected button value
 * @param config Modal configuration
 * @returns Promise that resolves with the selected button value, or null if cancelled
 */
export function showConfirmationModal(config: ModalConfig): Promise<string | null> {
  return new Promise((resolve) => {
    // Create modal container
    const modal = document.createElement('div');
    modal.className = `confirmation-modal-overlay ${config.modalType || 'info'}`;
    
    // Get icon based on modal type
    const iconMap = {
      warning: '⚠️',
      danger: '❌',
      info: 'ℹ️'
    };
    const icon = iconMap[config.modalType || 'info'];
    
    // Build buttons HTML
    const buttonsHTML = config.buttons.map(button => {
      const defaultClass = button.isDefault ? 'default' : '';
      return `<button 
        class="modal-btn modal-btn-${button.style} ${defaultClass}" 
        data-value="${button.value}"
      >${button.label}</button>`;
    }).join('');
    
    modal.innerHTML = `
      <div class="confirmation-modal-content">
        <div class="confirmation-modal-header">
          <span class="confirmation-modal-icon">${icon}</span>
          <h3>${config.title}</h3>
        </div>
        <div class="confirmation-modal-body">
          <p>${config.message}</p>
        </div>
        <div class="confirmation-modal-footer">
          ${buttonsHTML}
        </div>
      </div>
    `;
    
    // Add to DOM
    document.body.appendChild(modal);
    
    // Add entrance animation
    setTimeout(() => {
      modal.classList.add('show');
    }, 10);
    
    // Button click handler
    const handleButtonClick = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('modal-btn')) {
        const value = target.getAttribute('data-value');
        closeModal(value);
      }
    };
    
    // Background click handler (if enabled)
    const handleBackgroundClick = (e: Event) => {
      if (config.allowBackgroundClose && e.target === modal) {
        closeModal(null);
      }
    };
    
    // Escape key handler
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Find default button or use null
        const defaultButton = config.buttons.find(btn => btn.isDefault);
        closeModal(defaultButton?.value || null);
      }
    };
    
    // Enter key handler - trigger default button
    const handleEnterKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        const defaultButton = config.buttons.find(btn => btn.isDefault);
        if (defaultButton) {
          closeModal(defaultButton.value);
        }
      }
    };
    
    // Close modal function
    const closeModal = (value: string | null) => {
      // Remove event listeners
      modal.removeEventListener('click', handleButtonClick);
      modal.removeEventListener('click', handleBackgroundClick);
      document.removeEventListener('keydown', handleEscapeKey);
      document.removeEventListener('keydown', handleEnterKey);
      
      // Add exit animation
      modal.classList.remove('show');
      
      // Remove from DOM after animation
      setTimeout(() => {
        document.body.removeChild(modal);
        resolve(value);
      }, 200);
    };
    
    // Attach event listeners
    modal.addEventListener('click', handleButtonClick);
    modal.addEventListener('click', handleBackgroundClick);
    document.addEventListener('keydown', handleEscapeKey);
    document.addEventListener('keydown', handleEnterKey);
    
    // Focus the modal for keyboard events
    const firstButton = modal.querySelector('.modal-btn') as HTMLElement;
    firstButton?.focus();
  });
}

/**
 * Quick helper for simple yes/no confirmations
 */
export async function confirmDialog(title: string, message: string): Promise<boolean> {
  const result = await showConfirmationModal({
    title,
    message,
    modalType: 'warning',
    buttons: [
      { label: 'Yes', value: 'yes', style: 'primary' },
      { label: 'No', value: 'no', style: 'secondary', isDefault: true }
    ]
  });
  
  return result === 'yes';
}

/**
 * Quick helper for dangerous actions (like delete)
 */
export async function confirmDangerousAction(title: string, message: string, actionLabel: string = 'Delete'): Promise<boolean> {
  const result = await showConfirmationModal({
    title,
    message,
    modalType: 'danger',
    buttons: [
      { label: actionLabel, value: 'confirm', style: 'danger' },
      { label: 'Cancel', value: 'cancel', style: 'secondary', isDefault: true }
    ]
  });
  
  return result === 'confirm';
}
